import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readFormats, regelnVomTyp } from "@/office/excel/formate";
import { hatBezug } from "@/office/excel/formeln";
import { nichtPruefbar } from "../schnittstelle";

export const lektionRegeln: Lesson = {
  id: "regeln",
  title: "Regeln statt Handarbeit",
  summary: "Eine Farbe, die aus dem Wert folgt, bleibt richtig – auch wenn sich der Wert ändert.",
  durationMinutes: 12,
  learningGoals: [
    "Eine bedingte Formatierung anlegen und ihren Geltungsbereich verstehen",
    "Den Schwellenwert in eine Zelle auslagern statt ihn einzutippen",
    "Regeln verwalten, ordnen und wieder loswerden",
  ],
  steps: [
    {
      id: "warum-bedingt",
      title: "Der Unterschied zu einer gemalten Farbe",
      explanation: [
        "Eine Zelle rot einzufärben, weil der Wert zu niedrig ist, dauert zwei " +
          "Sekunden – und ist nach der nächsten Datenaktualisierung falsch. Die Farbe " +
          "klebt an der Zelle, nicht am Wert. Steigt die Zahl, bleibt sie rot; kommt " +
          "eine neue Zeile dazu, bleibt die farblos, obwohl sie es nicht sein sollte.",
        "Die bedingte Formatierung dreht das um: Nicht die Zelle bekommt eine Farbe, " +
          "sondern eine Regel bekommt einen Geltungsbereich. Excel prüft bei jeder " +
          "Änderung neu, welche Zellen die Bedingung erfüllen, und färbt entsprechend. " +
          "Die Darstellung folgt damit den Daten, statt hinter ihnen herzuhinken.",
        "Das ist derselbe Gedanke wie bei den Formatvorlagen und den Tabellen: Statt " +
          "einen Zustand festzuhalten, wird eine Regel hinterlegt. Und wie dort gilt: " +
          "Wer beides mischt – gemalte Farben und bedingte Regeln im selben Bereich –, " +
          "hat den schlechtesten aller Zustände, weil man nicht mehr sieht, was woher " +
          "kommt.",
        "Zu finden ist alles unter „Start“ → „Bedingte Formatierung“. Die oberen " +
          "Einträge sind fertige Regeln für die häufigsten Fälle, ganz unten steht " +
          "„Regeln verwalten“ – der Dialog, in dem alle Regeln einer Auswahl " +
          "aufgelistet sind und in dem sich ihre Reihenfolge ändern lässt.",
      ],
      keyPoints: [
        "Eine gemalte Farbe klebt an der Zelle, eine Regel folgt dem Wert.",
        "Start → Bedingte Formatierung → Regeln verwalten zeigt alles Vorhandene.",
        "Gemalte Farben und Regeln im selben Bereich nicht mischen.",
      ],
    },
    {
      id: "erste-regel",
      title: "Die erste Regel",
      explanation: [
        "Der schnellste Einstieg sind die fertigen Regeln unter „Regeln zum " +
          "Hervorheben von Zellen“: größer als, kleiner als, zwischen, gleich, " +
          "Textinhalt, Datum, doppelte Werte. Bereich markieren, Regel wählen, Wert " +
          "eintragen, Format aus der Liste nehmen – fertig.",
        "Wichtig ist der Geltungsbereich, den Excel dabei festhält. Markierst du nur " +
          "eine Zelle und legst die Regel an, gilt sie auch nur für diese eine. Neue " +
          "Zeilen unterhalb sind nicht erfasst. Deshalb: entweder gleich die ganze " +
          "Spalte markieren – oder, besser, die Regel innerhalb einer Tabelle anlegen, " +
          "denn dort erweitert sich der Geltungsbereich beim Wachsen automatisch mit.",
        "Der Geltungsbereich lässt sich nachträglich im Dialog „Regeln verwalten“ in " +
          "der Spalte „Wird angewendet auf“ ändern. Dort steht ein normaler " +
          "Bereichsbezug, den man überschreiben kann – nützlich, wenn eine Regel nur " +
          "für die halbe Spalte gilt.",
        "Zum Löschen einer Regel: nicht die Formatierung löschen, sondern die Regel. " +
          "Unter „Bedingte Formatierung“ → „Regeln löschen“ lässt sich wählen, ob nur " +
          "die markierten Zellen oder das ganze Blatt betroffen sind. Das zweite ist " +
          "gründlich und selten das, was man will.",
      ],
      keyPoints: [
        "Regeln zum Hervorheben von Zellen decken die häufigsten Fälle ab.",
        "Der Geltungsbereich entsteht aus der Markierung – am besten die ganze Spalte.",
        "In einer Tabelle wächst der Geltungsbereich automatisch mit.",
      ],
      task: {
        instruction:
          "Lege für eine deiner Zahlenspalten eine bedingte Formatierung an, die " +
          "auffällige Werte hervorhebt.",
        hint: "Spalte markieren, dann Start → Bedingte Formatierung → Regeln zum Hervorheben von Zellen → Größer als.",
        check: async () => {
          const formate = await readFormats();
          if (formate.conditionalFormats === null) {
            return nichtPruefbar(
              "Die bedingten Formatierungen eines Bereichs",
              "1.6",
              "Markiere deine Spalte und sieh unter „Start“ → „Bedingte Formatierung“ → „Regeln verwalten“ nach.",
              true,
            );
          }

          const regeln = formate.conditionalFormats;
          if (regeln.length === 0) {
            return fail(
              "In der Arbeitsmappe ist noch keine bedingte Formatierung hinterlegt.",
              "Markiere eine Zahlenspalte und wähle Start → Bedingte Formatierung → " +
                "Regeln zum Hervorheben von Zellen → Größer als.",
            );
          }

          const erste = regeln[0];
          return pass(
            `${regeln.length} ${regeln.length === 1 ? "Regel" : "Regeln"} gefunden, zum Beispiel eine ` +
              `vom Typ „${erste.type}“ auf „${erste.sheetName}“ über ${erste.address ?? "einen Bereich"}.`,
          );
        },
      },
    },
    {
      id: "schwelle-als-bezug",
      title: "Der Schwellenwert gehört in eine Zelle",
      explanation: [
        "Hier kehrt der rote Faden aus dem Formelmodul zurück. Eine Regel „größer als " +
          "1000“ hat den Schwellenwert in der Regel stehen – unsichtbar, weil niemand " +
          "den Regeldialog öffnet, um nachzusehen. Ändert sich die Schwelle, muss " +
          "jede Regel einzeln angefasst werden.",
        "Statt die Zahl einzutippen, lässt sich auf eine Zelle zeigen. Im Dialog " +
          "„Größer als“ genügt es, statt der Zahl die Zelle anzuklicken; Excel trägt " +
          "dann =$B$1 ein. Wichtig sind hier die Dollarzeichen: Bei einer Regel, die " +
          "für einen ganzen Bereich gilt, würde ein relativer Bezug für jede Zelle " +
          "mitwandern – was in manchen Fällen genau richtig ist, beim Schwellenwert " +
          "aber nicht.",
        "Der Gewinn ist derselbe wie bei den Formeln: Die Schwelle steht sichtbar auf " +
          "dem Blatt, mit einer Beschriftung daneben, und lässt sich ändern, ohne " +
          "einen Dialog zu öffnen. Wer die Darstellung ausprobieren will, tippt " +
          "verschiedene Werte in die Zelle und sieht sofort, wie sich die Färbung " +
          "verschiebt.",
        "Eine Einschränkung gibt es: In älteren Excel-Versionen muss die Zelle, auf " +
          "die eine bedingte Formatierung verweist, auf demselben Blatt liegen. Wer " +
          "die Schwellen auf einem Blatt „Einstellungen“ sammeln will, legt dafür auf " +
          "dem Datenblatt eine Hilfszelle an, die von dort liest.",
      ],
      keyPoints: [
        "Im Regeldialog statt der Zahl die Zelle anklicken – Excel trägt =$B$1 ein.",
        "Die Dollarzeichen sind hier Pflicht, sonst wandert die Schwelle mit.",
        "Verweise auf andere Blätter funktionieren nicht in jeder Excel-Version.",
      ],
      task: {
        instruction:
          "Ändere eine deiner Regeln so, dass der Schwellenwert nicht in der Regel " +
          "steht, sondern aus einer Zelle kommt.",
        hint: "Start → Bedingte Formatierung → Regeln verwalten → Regel bearbeiten. Dort statt der Zahl die Zelle anklicken.",
        check: async () => {
          const formate = await readFormats();
          if (formate.conditionalFormats === null) {
            return nichtPruefbar(
              "Die Regeln einer bedingten Formatierung",
              "1.6",
              "Öffne „Regeln verwalten“ und sieh nach, ob im Feld für den Wert ein Zellbezug statt einer Zahl steht.",
              true,
            );
          }

          const zellwertregeln = regelnVomTyp(formate, "CellValue");
          const alleRegeln = formate.conditionalFormats;

          if (alleRegeln.length === 0) {
            return fail(
              "In der Arbeitsmappe ist noch keine bedingte Formatierung hinterlegt.",
              "Löse zuerst die Aufgabe im vorigen Schritt.",
            );
          }

          if (zellwertregeln.length === 0) {
            return fail(
              `Es gibt ${alleRegeln.length} ${alleRegeln.length === 1 ? "Regel" : "Regeln"}, aber keine, ` +
                `die einen Zellwert mit einer Schwelle vergleicht (Typen: ` +
                `${[...new Set(alleRegeln.map((regel) => regel.type))].join(", ")}).`,
              "Lege eine Regel vom Typ „Größer als“ oder „Kleiner als“ an – nur dort gibt es einen " +
                "Schwellenwert, der sich auslagern lässt.",
            );
          }

          const mitBezug = zellwertregeln.filter(
            (regel) => regel.formula1 !== null && hatBezug(regel.formula1),
          );

          if (mitBezug.length === 0) {
            const erste = zellwertregeln[0];
            return fail(
              `Die Regel auf „${erste.sheetName}“ vergleicht mit dem festen Wert ` +
                `„${erste.formula1 ?? "unbekannt"}“ statt mit einer Zelle.`,
              "Öffne „Regeln verwalten“, bearbeite die Regel und klicke statt der Zahl die Zelle an, " +
                "in der die Schwelle steht. Excel trägt dann =$B$1 ein.",
            );
          }

          const erste = mitBezug[0];
          return pass(
            `Die Regel auf „${erste.sheetName}“ vergleicht mit „${erste.formula1}“ – der Schwellenwert ` +
              "steht sichtbar auf dem Blatt und lässt sich ändern, ohne einen Dialog zu öffnen.",
          );
        },
      },
    },
  ],
};
